import { Check } from "lucide-react";
import React from "react";
import { useIntl } from "react-intl";
import { CreateCheckoutSessionBody } from "~/api/model";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "~/components/ui/card";
import { cn } from "~/lib/utils";

type OrganizationPlan = Exclude<
  NonNullable<CreateCheckoutSessionBody>["organizationPlan"],
  undefined
>;

export interface PricingTier {
  id: string;
  nameId: string;
  nameDefault: string;
  priceId: string;
  priceDefault: string;
  unitId: string;
  unitDefault: string;
  subtitleId: string;
  subtitleDefault: string;
  equivalentNoteId?: string;
  equivalentNoteDefault?: string;
  features: {
    id: string;
    defaultMessage: string;
  }[];
  ctaId: string;
  ctaDefault: string;
  highlighted?: boolean;
  badgeId?: string;
  badgeDefault?: string;
  plan: OrganizationPlan;
}

interface PricingCardProps {
  tier: PricingTier;
  onSelect: (tierId: string) => void;
  isPending: boolean;
  isCurrentPlan?: boolean;
  isDowngrade?: boolean;
  isUpgrade?: boolean;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  tier,
  onSelect,
  isPending,
  isCurrentPlan = false,
  isDowngrade = false,
  isUpgrade = false,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Card
      className={cn(
        "relative flex h-full flex-col transition-shadow",
        isCurrentPlan ||
          (tier.highlighted && !isCurrentPlan && !isDowngrade && !isUpgrade)
          ? "border-blue-600 shadow-lg ring-1 ring-blue-600"
          : "border-border shadow-xs hover:shadow-md"
      )}
    >
      {tier.highlighted && tier.badgeId && !isCurrentPlan && !isDowngrade && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="whitespace-nowrap rounded-full bg-blue-600 px-3 py-1 text-[11px] uppercase tracking-wider text-white hover:bg-blue-600">
            {formatMessage({
              id: tier.badgeId,
              defaultMessage: tier.badgeDefault,
            })}
          </Badge>
        </div>
      )}

      <CardHeader className="pb-0">
        <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {formatMessage({
            id: tier.nameId,
            defaultMessage: tier.nameDefault,
          })}
        </h3>

        <div className="mt-2 flex items-baseline gap-0.5">
          <span className="text-3xl font-semibold tracking-tight text-foreground">
            {formatMessage({
              id: tier.priceId,
              defaultMessage: tier.priceDefault,
            })}
          </span>
          <span className="text-sm text-muted-foreground">
            {formatMessage({
              id: tier.unitId,
              defaultMessage: tier.unitDefault,
            })}
          </span>
        </div>

        <p className="mt-1 text-[13px] text-muted-foreground">
          {formatMessage({
            id: tier.subtitleId,
            defaultMessage: tier.subtitleDefault,
          })}
        </p>
      </CardHeader>

      <CardContent className="flex-1 pt-4">
        {tier.equivalentNoteId && tier.equivalentNoteDefault && (
          <div className="mb-4 rounded-md bg-emerald-50 px-2.5 py-1.5 text-[12px] font-medium text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
            {formatMessage({
              id: tier.equivalentNoteId,
              defaultMessage: tier.equivalentNoteDefault,
            })}
          </div>
        )}

        <ul className="space-y-2.5">
          {tier.features.map((feature) => (
            <li
              key={feature.id}
              className="flex items-start gap-2 text-sm text-foreground"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
              <span>
                {formatMessage({
                  id: feature.id,
                  defaultMessage: feature.defaultMessage,
                })}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="flex-col gap-3">
        <p className="text-xs text-muted-foreground">+ $49/seat/mo</p>
        {isCurrentPlan ? (
          <p className="flex h-9 w-full items-center justify-center text-sm font-medium text-muted-foreground">
            {formatMessage({
              id: "billing.plan.yourCurrentPlan",
              defaultMessage: "Your current plan",
            })}
          </p>
        ) : (
          <Button
            className="w-full rounded-lg"
            variant={
              isDowngrade ? "outline" : tier.highlighted ? "default" : "outline"
            }
            onClick={() => onSelect(tier.id)}
            disabled={isPending}
          >
            {isDowngrade
              ? formatMessage({
                  id: "billing.plan.downgrade",
                  defaultMessage: "Downgrade",
                })
              : formatMessage({
                  id: tier.ctaId,
                  defaultMessage: tier.ctaDefault,
                })}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
